import React, { useEffect } from 'react';
import KACommon from './KACommon';
import KAOverview from './KAOverview';
import { useKeywordAssist } from '../Providers/KAProviders';
import KADetailedView from './KADetailedView';
import useScrollToTop from 'src/hooks/use-Scroll-to-top';
import { useSelector } from 'react-redux';
import { RootState } from 'src/redux/store';
import KANoDataFound from './KANoDataFound';
import { CircularProgress } from '@mui/material';
import { Card as SEOCard } from 'src/components/UI/Card';

const KeywordAssist = ({ isProcessing }: { isProcessing?: boolean }) => {
  const { _id: siteId } = useSelector(
    (state: RootState) => state?.siteAudit?.siteAuditDetails
  );
  const siteSoftDeleted = useSelector(
    (state: RootState) => state?.siteAudit?.isSiteSoftDeleted
  );
  const {
    state: { currentView, keywordsData },
    changeCurrentView
  } = useKeywordAssist();

  const { targetRef } = useScrollToTop({ deps: [currentView] });

  // Handle check if backend has no data
  const hasNoData =
    !isProcessing &&
    (!keywordsData ||
      (typeof keywordsData === 'object' && Object.keys(keywordsData).length === 0));

  useEffect(() => {
    // if siteId change, then redirect to overview page
    if (siteId) {
      changeCurrentView('overview');
    }
  }, [siteId]);

  return (
    <div
      className="-mt-8 pt-8"
      style={{
        minHeight: 'calc(100vh - 51px)',
        backgroundColor: 'var(--color-bg-cream)'
      }}
      ref={targetRef as React.RefObject<HTMLDivElement>}
    >
      <div aria-label="keyword-assist-common-header-and-progress">
        <KACommon />
      </div>

      {isProcessing && (
        <div className="my-4">
          <SEOCard>
            <div className="px-6">
              <p className="text-primary font-semibold text-3xl">Keyword Assist</p>
              <div className="flex flex-col items-center justify-center gap-6 p-6 pb-6 w-full mt-2.5 mb-2.5">
                <div className="flex flex-col items-center justify-center gap-3 text-center min-h-[16rem]">
                  <CircularProgress size={64} />
                  <p className="font-semibold text-lg text-[#1D2939] mt-2">
                    Orbit AI Keyword Assist is running...
                  </p>
                  <p className="font-normal text-sm text-[#667085] max-w-md">
                    This may take a few moments while we analyze your site and discover high-value keywords.
                  </p>
                </div>
              </div>
            </div>
          </SEOCard>
        </div>
      )}

      {hasNoData && !isProcessing && (
        <div className="my-4">
          <KANoDataFound siteSoftDeleted={siteSoftDeleted} />
        </div>
      )}

      {currentView === 'overview' && !hasNoData && !isProcessing && (
        <div aria-label="keyword-assist-overview">
          <KAOverview />
        </div>
      )}

      {currentView === 'detailed' && !hasNoData && !isProcessing && (
        <div aria-label="keyword-assist-detailed">
          <KADetailedView />
        </div>
      )}
    </div>
  );
};

export default KeywordAssist;
